import { DefaultService } from "@/api/mih/services/DefaultService";
import { OpenAPI } from "@/api/mih/core/OpenAPI";
import { OpenAPI as OpenAPI_B2B } from "@/api/b2b/core/OpenAPI";
import { useAppStore, type TAppStore } from "~/store/app";
import type { TUser } from "@/interfaces/app";
import { debounce } from "lodash";

export interface IAuth {
  constructor: Function;
  user: TUser | null;
  loggedIn: boolean;
  hasActiveSubscription: boolean;
  init: Function;
  fetchUser: Function;
  login: Function;
  logout: Function;
}

const LOGGED_IN_KEY = "isMihLoggedIn";

/*
  Please note notifyError plugin is not accessible here so the new setup
  for error notifications is created here.
*/
class MihAuth implements IAuth {
  private $store: TAppStore;

  constructor() {
    this.$store = useAppStore();
    OpenAPI.WITH_CREDENTIALS = true;
    OpenAPI_B2B.WITH_CREDENTIALS = true;
  }

  get user() {
    return this.$store.user;
  }

  get loggedIn() {
    const isLoggedIn = localStorage.getItem(LOGGED_IN_KEY);
    try {
      return JSON.parse(isLoggedIn ?? "") === true;
    } catch (_e) {
      return false;
    }
  }

  setLoggedIn() {
    localStorage.setItem(LOGGED_IN_KEY, "true");
  }

  setLoggedOut() {
    localStorage.removeItem(LOGGED_IN_KEY);
  }

  get hasActiveSubscription() {
    // TODO: Implement tokens/collections room check
    return this.user?.subscription?.status === "active";
  }

  async init() {
    return await this.fetchUser().catch(() => {});
  }

  fetchUser() {
    return DefaultService.getMihV1UserDetails()
      .then(async (user: TUser) => {
        if (user) {
          await this.$store.setUser(user);
          return user;
        } else {
          return Promise.reject(new Error("Invalid user returned"));
        }
      })
      .catch(async (e) => {
        await this.$store.setUser(null);
        return Promise.reject(e);
      });
  }

  logout(justClearState = false) {
    const clearState = async () => {
      this.setLoggedOut();
      await this.$store.setUser(null);
    };
    if (justClearState) {
      return clearState();
    }
    return DefaultService.postMihV1AuthLogout()
      .catch(() => {})
      .finally(() => {
        clearState();
      });
  }

  login(email: string, password: string) {
    return DefaultService.postMihV1AuthLogin({
      email,
      password,
    }).then(() => {
      return this.fetchUser();
    });
  }
}

declare module "#app" {
  interface NuxtApp {
    $auth: MihAuth;
  }
}

export default defineNuxtPlugin(() => {
  const $auth = new MihAuth();
  const fetchUserDebounced = debounce(() => $auth.fetchUser(), 50);

  return {
    provide: {
      auth: $auth,
      fetchUserDebounced,
    },
  };
});
